import { useTranslation } from "react-i18next";
import { createFormContext } from "@mantine/form";
import { CreateCampaignInquiryInput } from "src/graphql/generated/graphql";
import { DeepPartial } from "src/types/utils";

import { getFormValues } from "./utils";

export type CreateCampaignInquiryFormValues = DeepPartial<Omit<CreateCampaignInquiryInput, "tags" | "outcome">>;

export const [CreateCampaignInquiryFormProvider, useCreateCampaignInquiryFormContext, useForm] =
    createFormContext<CreateCampaignInquiryFormValues>();

/** A catalogue product that already owns an EAN the client is trying to claim. */
export interface EanConflict {
    productId: string;
    name: string;
    ean: string;
}

/**
 * Shared state for the EAN duplicate check (TTV2-1569), keyed by EAN so it does
 * not depend on a product's position in the list.
 *
 * It is held in a ref rather than in form values or React state because the two
 * readers need it in ways component state cannot serve: Mantine's `validate` is
 * synchronous, and the check is an async fetch — so the verdict has to be
 * somewhere the validator can read it later. Keeping it out of the form values
 * also keeps it out of the CreateCampaignInquiryInput payload.
 */
export interface EanCheckState {
    /** EAN -> the product that already has it. */
    conflicts: Record<string, EanConflict>;
    /** EANs with a lookup still in flight. */
    pending: Record<string, true>;
}

export type EanCheckRef = { current: EanCheckState };

export const createEanCheckState = (): EanCheckState => ({ conflicts: {}, pending: {} });

export const isEanCheckPending = (state?: EanCheckState) => Object.keys(state?.pending || {}).length > 0;

export const useCreateCampaignInquiryForm = (
    values?: Parameters<typeof useForm>[0],
    eanCheckRef?: EanCheckRef
): ReturnType<typeof useForm> => {
    const { t } = useTranslation(["common"]);

    return useForm({
        ...values,
        initialValues: getFormValues(values?.initialValues),
        validateInputOnBlur: true,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        validate: (values) => ({
            tenantId: !values?.tenantId ? t("campaign_error_missingtenant") : null,
            displayTenantIds: !(values?.displayTenantIds?.length > 0)
                ? t("campaign_error_missingdisplaytenants")
                : null,
            name: !(values?.name?.length > 0) ? t("campaign_error_missingname") : null,
            ...(values?.products || []).reduce((errors, product, index) => {
                // An EAN we already stock may only be used by linking to that
                // product, never by asking us to create a second one under it.
                // A product that IS linked is exempt: its EAN is meant to match.
                const isUnlinked = !product?.id && !!product?.ean;
                const hasEanConflict = isUnlinked && !!eanCheckRef?.current?.conflicts?.[product.ean];
                // A lookup still in flight has not ruled yet, so treat it as not
                // yet valid. That is what stops a client who types the thirteenth
                // digit and submits immediately from outrunning the check: they
                // get "checking EAN" and succeed a moment later.
                const isEanPending = isUnlinked && !!eanCheckRef?.current?.pending?.[product.ean];

                return {
                    ...errors,
                    [`products.${index}.ean`]:
                        product?.ean && !/^[0-9]{13}$/.test(product.ean)
                            ? t("campaign_error_invalidean")
                            : hasEanConflict
                                ? t("product_code_ean_alreadyexists")
                                : isEanPending
                                    ? t("product_code_ean_checking")
                                    : null,
                    [`products.${index}.category`]: !product?.category?.key
                        ? t("campaign_error_missingproductcategory")
                        : null,
                    [`products.${index}.name`]: !(product?.name?.length > 0)
                        ? t("campaign_error_missingproductname")
                        : null,
                };
            }, {}),
        }),
    });
};
